/* React Query rond de EMS-adapter. De client komt als argument binnen: in het
 * CRM is dat crmClient (de sessie van de ingelogde gebruiker), in de toetsen en
 * de browserproef een nagebootste transportlaag. */

import { useQuery } from '@tanstack/react-query';
import {
  haalAlles, haalEmsVanSite, haalSiteIdsMetEms, haalSiteVanLocatie, haalMaandrapport,
  EmsFout, type EmsClient,
} from '../data/leesEms';

/* Zelfde venster als DAGEN in web/src/App.jsx. */
export const EMS_DAGEN = 31;

/* Elke vijf minuten opnieuw, in de pas met de ophaaldienst (cron elke 5 min). React
   Query ververst niet zolang het tabblad verborgen is, dus een dashboard dat op
   de achtergrond openstaat vraagt niets. Het dashboard volgt de nieuwe meting
   alleen als niemand een eigen dag of kwartier koos; zie `keuze` in Dashboard. */
export const EMS_VERVERS_MS = 5 * 60_000;

/* Een weigering of een ontbrekende locatie wordt niet opnieuw geprobeerd; een
   onderbroken paginering of een netwerkfout één keer. */
export function opnieuw(aantal: number, fout: unknown): boolean {
  const soort = fout instanceof EmsFout ? fout.soort : 'fout';
  if (soort === 'geen-toegang' || soort === 'niet-gevonden' || soort === 'configuratie' || soort === 'invoer') return false;
  return aantal < 1;
}

export function useEmsDashboard(client: EmsClient, locatieId: string | undefined) {
  return useQuery({
    queryKey: ['ems', 'dashboard', locatieId, EMS_DAGEN],
    queryFn: () => haalAlles(client, locatieId as string, { dagenTerug: EMS_DAGEN }),
    enabled: Boolean(locatieId),
    retry: opnieuw,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchInterval: EMS_VERVERS_MS,
  });
}

/** De actieve EMS-locaties van één site, en als die er niet zijn: of er een uitgezette hangt. */
export function useEmsVanSite(client: EmsClient, siteId: string | undefined, { enabled = true } = {}) {
  return useQuery({
    queryKey: ['ems', 'site', siteId],
    queryFn: () => haalEmsVanSite(client, siteId as string),
    enabled: Boolean(siteId) && enabled,
    retry: opnieuw,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export const SITES_MET_EMS_SLEUTEL = ['ems', 'sites-met-ems'] as const;

/** Welke sites energiemonitoring hebben, voor de hele lijst in één vraag. */
export function useSiteIdsMetEms(client: EmsClient) {
  return useQuery({
    queryKey: SITES_MET_EMS_SLEUTEL,
    queryFn: () => haalSiteIdsMetEms(client),
    retry: opnieuw,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** De site van één EMS-locatie, voor /crm/ems/:locatieId. */
export function useSiteVanLocatie(client: EmsClient, locatieId: string | undefined) {
  return useQuery({
    queryKey: ['ems', 'site-van-locatie', locatieId],
    queryFn: () => haalSiteVanLocatie(client, locatieId as string),
    retry: opnieuw,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * De dagtotalen voor het tabblad Maand (ems.dagtotaal, migratie 109, ADR 0033).
 * Pas als de locatie bekend is: de zone komt uit ems.locatie. Dagrijen zijn
 * klein, dus de hele historie komt in één keer mee; verversen hoeft maar eens
 * per uur (de nachtelijke ronde schrijft één keer per dag).
 */
export function useEmsMaandrapport(client: EmsClient, locatieId: string | undefined, zone: string | undefined) {
  return useQuery({
    queryKey: ['ems', 'maandrapport', locatieId, zone],
    queryFn: () => haalMaandrapport(client, locatieId as string, zone as string),
    enabled: Boolean(locatieId) && Boolean(zone),
    retry: opnieuw,
    staleTime: 60 * 60_000,
    refetchOnWindowFocus: false,
  });
}
